import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { axiosApi, downloadFile } from "../helpers";
import { ENDPOINTS_MARKS, URL_MARKS } from "../constants/api_endpoints";
import {
	IAddMarksProps,
	IAddMarksResponse,
	IExportMarksProps,
	IImportMarksProps,
	IMarksListProps,
	IMarksListResponse,
	IMarksResponse,
	IMarksState,
	IModifyMarksProps,
	IRenameMarksProps,
} from "../types/marksTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../types/statusTypes";

const initialState: IMarksState = {
	marksList: {
		data: [],
		activeMarksId: null,
		activeMarksName: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	marks: {
		data: [],
		selectedRow: 0,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addMarks: {
		id: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modifyMarks: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deleteMarks: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	renameMarks: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	importMarks: {
		progress: 0,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	exportMarks: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение списка словарей меток
export const getMarksList = createAsyncThunk("marks/list", async ({ serviceType }: IMarksListProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IMarksListResponse> = await axiosApi.get(`${URL_MARKS}/${ENDPOINTS_MARKS.LIST}/${serviceType}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// получение словаря меток
export const getMarks = createAsyncThunk("marks/info", async (marksId: string, { rejectWithValue, signal }) => {
	try {
		const response: AxiosResponse<IMarksResponse> = await axiosApi.get(`${URL_MARKS}/${ENDPOINTS_MARKS.GET}/${marksId}`, {
			signal,
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// добавление словаря меток
export const addMarks = createAsyncThunk("marks/add", async ({ marksName, serviceType, type }: IAddMarksProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddMarksResponse> = await axiosApi.post(`${URL_MARKS}/${ENDPOINTS_MARKS.ADD}`, {
			json: JSON.stringify({ name: marksName, service: serviceType, type }),
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// изменение словаря меток
export const modifyMarks = createAsyncThunk("marks/modify", async ({ marksId, data }: IModifyMarksProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_MARKS}/${ENDPOINTS_MARKS.MODIFY}/${marksId}`, {
			json: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// удаление словаря меток
export const deleteMarks = createAsyncThunk("marks/delete", async (marksId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_MARKS}/${ENDPOINTS_MARKS.DELETE}/${marksId}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// переименование словаря меток
export const renameMarks = createAsyncThunk("marks/rename", async ({ newMarksName, marksId }: IRenameMarksProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_MARKS}/${ENDPOINTS_MARKS.RENAME}${marksId}`, {
			name: newMarksName,
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// импорт словаря меток из .csv файла
export const importMarks = createAsyncThunk("marks/import", async ({ marksId, formData }: IImportMarksProps, { dispatch, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(
			`${URL_MARKS}/${ENDPOINTS_MARKS.IMPORT}/${marksId}`,
			{
				csv: formData.get("file"),
			},
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (progressEvent) => {
					if (progressEvent.progress) {
						const progress = Math.round(progressEvent.progress * 100);
						dispatch(changeProgressImportMarks(progress));
					}
				},
			}
		);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// экспорт словаря меток (.csv файл)
export const exportMarks = createAsyncThunk("marks/export", async ({ marksId, marksName }: IExportMarksProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.get(`${URL_MARKS}/${ENDPOINTS_MARKS.EXPORT}/${marksId}`, { responseType: "blob" });
		if (response.data instanceof Blob) {
			downloadFile(response.data, `${marksName}.csv`);
		} else return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

const marksSlice = createSlice({
	name: "marks",
	initialState,
	reducers: {
		// добавление id и имени активного словаря меток
		addingActiveMarksIdAndName: (state, action: PayloadAction<{ id: string; name: string }>) => {
			state.marksList.activeMarksId = action.payload.id;
			state.marksList.activeMarksName = action.payload.name;
		},
		// изменение имени корпуса (для переименовывания) без перезагрузки страницы
		// editCorpusName: (state, action: PayloadAction<{ prevName: string, newName: string }>) => {
		// 	state.corpusList.data = state.corpusList.data.map(corpusName => {
		// 		// если изменяемое имя совпало с именем из списка корпусов - меняем на новое
		// 		if (action.payload.prevName === corpusName) return action.payload.newName;
		// 		else return corpusName; // иначе оставляем как есть
		// 	});
		// 	state.corpus.corpusName = action.payload.newName; // меняем активное имя на новое
		// },
		// изменение прогресса импорта словаря меток в %
		changeProgressImportMarks: (state, action: PayloadAction<number>) => {
			state.importMarks.progress = action.payload;
		},
		// изменение выбранной строки
		changeSelectedRow: (state, action: PayloadAction<number>) => {
			state.marks.selectedRow = action.payload;
		},
		// изменение ячейки таблицы словаря меток (по факту изменение строки полностью)
		editMarksDataCell: (state, action: PayloadAction<{ index: number; mark?: string; phrases?: string[]; threshold?: number }>) => {
			state.marks.data[action.payload.index] = [
				action.payload.mark ?? state.marks.data[action.payload.index][0],
				action.payload.phrases || state.marks.data[action.payload.index][1],
				action.payload.threshold || state.marks.data[action.payload.index][2],
			];
		},
		// добавление строки в таблицу меток
		addRow: (state, action: PayloadAction<[string, string[], number]>) => {
			state.marks.data.push(action.payload);
		},
		// удаление строки из таблицы меток
		deleteRow: (state, action: PayloadAction<number>) => {
			state.marks.data.splice(action.payload, 1);
		},
		// добавление фразы
		addPhrase: (state, action: PayloadAction<{ rowIdx: number; phrase: string }>) => {
			state.marks.data[action.payload.rowIdx][1].push(action.payload.phrase);
		},
		// изменение фразы
		editPhrase: (state, action: PayloadAction<{ rowIdx: number; phraseIdx: number; phrase: string }>) => {
			state.marks.data[action.payload.rowIdx][1].splice(action.payload.phraseIdx, 1, action.payload.phrase);
		},
		// удаление фразы
		deletePhrase: (state, action: PayloadAction<{ rowIdx: number; phraseIdx: number }>) => {
			state.marks.data[action.payload.rowIdx][1].splice(action.payload.phraseIdx, 1);
		},
		// очистка state
		clearState: (state) => {
			state.marksList = initialState.marksList;
			state.marks = initialState.marks;
			state.addMarks = initialState.addMarks;
			state.modifyMarks = initialState.modifyMarks;
			state.deleteMarks = initialState.deleteMarks;
			state.renameMarks = initialState.renameMarks;
			state.importMarks = initialState.importMarks;
			state.exportMarks = initialState.exportMarks;
		},
		// очистка списка словарей меток
		clearMarksList: (state) => {
			state.marksList = initialState.marksList;
		},
		// очистка словаря меток
		clearMarks: (state) => {
			state.marks = initialState.marks;
		},
		// очистка статуса добавления словаря меток
		clearAddMarks: (state) => {
			state.addMarks = initialState.addMarks;
		},
		// очистка статуса изменения словаря меток
		clearModifyMarks: (state) => {
			state.modifyMarks = initialState.modifyMarks;
		},
		// очистка статуса удаления словаря меток
		clearDeleteMarks: (state) => {
			state.deleteMarks = initialState.deleteMarks;
		},
		// очистка статуса переименования словаря меток
		clearRenameMarks: (state) => {
			state.renameMarks = initialState.renameMarks;
		},
		// очистка статуса импорта словаря меток
		clearImportMarks: (state) => {
			state.importMarks = initialState.importMarks;
		},
		// очистка статуса экспорта словаря меток
		clearExportMarks: (state) => {
			state.exportMarks = initialState.exportMarks;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getMarksList.pending, (state) => {
				state.marksList.status = RequestStatus.LOADING;
			})
			.addCase(getMarksList.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "result" in action.payload && Array.isArray(action.payload.result)) {
					state.marksList.status = RequestStatus.IDLE;
					state.marksList.data = action.payload.result
						.filter((marksItem) => marksItem.type === "marks")
						.sort((a, b) => {
							if (a.name > b.name) return 1;
							else if (a.name < b.name) return -1;
							else return 0;
						});
				} else state.marksList.status = RequestStatus.FAILED;
			})
			.addCase(getMarksList.rejected, (state, action: PayloadAction<unknown>) => {
				state.marksList.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.marksList.error = action.payload.response?.data.error;
					state.marksList.message = action.payload.response?.data.message;
				}
			})
			.addCase(getMarks.pending, (state) => {
				state.marks.status = RequestStatus.LOADING;
			})
			.addCase(getMarks.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "result" in action.payload && action.payload.result && Array.isArray(action.payload.result.data)) {
					state.marks.status = RequestStatus.IDLE;
					state.marks.data = action.payload.result.data;
				} else state.marks.status = RequestStatus.FAILED;
			})
			.addCase(getMarks.rejected, (state, action: PayloadAction<unknown>) => {
				state.marks.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.marks.error = action.payload.response?.data.error;
					state.marks.message = action.payload.response?.data.message;
				}
			})
			.addCase(addMarks.pending, (state) => {
				state.addMarks.status = RequestStatus.LOADING;
			})
			.addCase(addMarks.fulfilled, (state, action) => {
				if (action.payload) {
					state.addMarks.status = RequestStatus.IDLE;
					state.addMarks.error = action.payload.error;
					state.addMarks.message = action.payload.message;
					if (action.payload.id) {
						state.addMarks.id = action.payload.id;
					}
				} else state.addMarks.status = RequestStatus.FAILED;
			})
			.addCase(addMarks.rejected, (state, action: PayloadAction<unknown>) => {
				state.addMarks.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.addMarks.error = action.payload.response?.data.error;
					state.addMarks.message = action.payload.response?.data.message;
				}
			})
			.addCase(modifyMarks.pending, (state) => {
				state.modifyMarks.status = RequestStatus.LOADING;
			})
			.addCase(modifyMarks.fulfilled, (state, action) => {
				if (action.payload) {
					state.modifyMarks.status = RequestStatus.IDLE;
					state.modifyMarks.error = action.payload.error;
					state.modifyMarks.message = action.payload.message;
				} else state.modifyMarks.status = RequestStatus.FAILED;
			})
			.addCase(modifyMarks.rejected, (state, action: PayloadAction<unknown>) => {
				state.modifyMarks.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.modifyMarks.error = action.payload.response?.data.error;
					state.modifyMarks.message = action.payload.response?.data.message;
				}
			})
			.addCase(deleteMarks.pending, (state) => {
				state.deleteMarks.status = RequestStatus.LOADING;
			})
			.addCase(deleteMarks.fulfilled, (state, action) => {
				if (action.payload) {
					state.deleteMarks.status = RequestStatus.IDLE;
					state.deleteMarks.error = action.payload.error;
					state.deleteMarks.message = action.payload.message;
				} else state.deleteMarks.status = RequestStatus.FAILED;
			})
			.addCase(deleteMarks.rejected, (state, action: PayloadAction<unknown>) => {
				state.deleteMarks.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.deleteMarks.error = action.payload.response?.data.error;
					state.deleteMarks.message = action.payload.response?.data.message;
				}
			})
			.addCase(renameMarks.pending, (state) => {
				state.renameMarks.status = RequestStatus.LOADING;
			})
			.addCase(renameMarks.fulfilled, (state, action) => {
				if (action.payload) {
					state.renameMarks.status = RequestStatus.IDLE;
					state.renameMarks.error = action.payload.error;
					state.renameMarks.message = action.payload.message;
				} else state.renameMarks.status = RequestStatus.FAILED;
			})
			.addCase(renameMarks.rejected, (state, action: PayloadAction<unknown>) => {
				state.renameMarks.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.renameMarks.error = action.payload.response?.data.error;
					state.renameMarks.message = action.payload.response?.data.message;
				}
			})
			.addCase(importMarks.pending, (state) => {
				state.importMarks.status = RequestStatus.LOADING;
			})
			.addCase(importMarks.fulfilled, (state, action) => {
				if (action.payload) {
					state.importMarks.status = RequestStatus.IDLE;
					state.importMarks.error = action.payload.error;
					state.importMarks.message = action.payload.message;
				} else state.importMarks.status = RequestStatus.FAILED;
			})
			.addCase(importMarks.rejected, (state, action: PayloadAction<unknown>) => {
				state.importMarks.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.importMarks.error = action.payload.response?.data.error;
					state.importMarks.message = action.payload.response?.data.message;
				}
			})
			.addCase(exportMarks.pending, (state) => {
				state.exportMarks.status = RequestStatus.LOADING;
			})
			.addCase(exportMarks.fulfilled, (state, action) => {
				state.exportMarks.status = RequestStatus.IDLE;
				if (action.payload) {
					state.exportMarks.error = action.payload.error;
					state.exportMarks.message = action.payload.message;
				}
			})
			.addCase(exportMarks.rejected, (state, action: PayloadAction<unknown>) => {
				state.exportMarks.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.exportMarks.error = action.payload.response?.data.error;
					state.exportMarks.message = action.payload.response?.data.message;
				}
			});
	},
});

export const {
	addingActiveMarksIdAndName,
	changeProgressImportMarks,
	changeSelectedRow,
	editMarksDataCell,
	addRow,
	deleteRow,
	addPhrase,
	editPhrase,
	deletePhrase,
	clearState,
	clearMarksList,
	clearMarks,
	clearAddMarks,
	clearModifyMarks,
	clearDeleteMarks,
	clearRenameMarks,
	clearImportMarks,
	clearExportMarks,
} = marksSlice.actions;

export const selectMarksList = (state: RootState) => state.marks.marksList;
export const selectMarks = (state: RootState) => state.marks.marks;
export const selectAddMarks = (state: RootState) => state.marks.addMarks;
export const selectModifyMarks = (state: RootState) => state.marks.modifyMarks;
export const selectDeleteMarks = (state: RootState) => state.marks.deleteMarks;
export const selectRenameMarks = (state: RootState) => state.marks.renameMarks;
export const selectImportMarks = (state: RootState) => state.marks.importMarks;
export const selectExportMarks = (state: RootState) => state.marks.exportMarks;

export default marksSlice.reducer;
